import "slick-carousel/slick/slick.css";
import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Link } from "react-router-dom";
import { SectionHeading, Subheading as SubheadingBase } from "components/misc/Headings.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import loveIllustrationImageSrc from "images/love.svg";
import { ReactComponent as StarIconBase } from "images/star-icon.svg";
import { ReactComponent as ArrowLeftIcon } from "images/arrow-left-2-icon.svg";
import { ReactComponent as ArrowRightIcon } from "images/arrow-right-2-icon.svg";

const API_URL = process.env.REACT_APP_IS_PROD == "true" ? process.env.REACT_APP_API_URL_PROD : process.env.REACT_APP_API_URL_DEV


// Styled components
const Row = tw.div`flex flex-col md:flex-row justify-between items-center`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = tw(Column)`md:w-5/12 xl:w-6/12 flex-shrink-0 relative`;
const TextColumn = styled(Column)(props => [
  tw`md:w-7/12 xl:w-6/12 mt-16 md:mt-0`,
  props.textOnLeft ? tw`md:pr-12 lg:pr-16 md:order-first` : tw`md:pl-12 lg:pl-16 md:order-last`
]);

const Image = styled.img(props => [
  props.imageRounded && tw`rounded`,
  props.imageBorder && tw`border`,
  props.imageShadow && tw`shadow`
]);

const Subheading = tw(SubheadingBase)`text-center md:text-left text-3xl`;
const Heading = tw(SectionHeading)`mt-4 font-black text-left text-3xl sm:text-4xl lg:text-5xl text-center md:text-left leading-tight`;
const Description = tw.p`mt-6 text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`;

const TestimonialSlider = styled(Slider)`
  ${tw`w-full mt-10 text-center md:text-left`}
  .slick-track {
    ${tw`flex`}
  }
  .slick-slide {
    ${tw`h-auto flex justify-center mb-1`}
  }
`;

const Testimonial = tw.div`outline-none h-full flex! flex-col`;
const StarsContainer = styled.div``;
const StarIcon = tw(StarIconBase)`inline-block w-5 h-5 text-orange-400 fill-current mr-1 last:mr-0`;
const TestimonialHeading = tw.div`mt-4 text-xl font-bold`;
const Quote = tw.blockquote`mt-4 mb-8 sm:mb-10 leading-relaxed font-medium text-gray-700`;

const CustomerInfoAndControlsContainer = tw.div`mt-auto flex justify-between items-center flex-col sm:flex-row`;

const CustomerInfo = tw.div`flex flex-col sm:flex-row items-center justify-center lg:justify-start`;
const CustomerProfilePicture = tw.img`rounded-full w-16 h-16 sm:w-20 sm:h-20`;
const CustomerTextInfo = tw.div`text-center md:text-left sm:ml-6 mt-2 sm:mt-0`;
const CustomerName = tw.h5`font-bold text-xl`;
const CustomerTitle = tw.p`font-medium text-secondary-100`;

const Controls = styled.div`
  ${tw`flex mt-8 justify-center md:justify-start`}
  .divider {
    ${tw`my-3 border-r`}
  }
`;
const ControlButton = styled.button`
  ${tw`mx-3 p-4 rounded-full transition duration-300 bg-gray-200 hover:bg-gray-300 text-[#0049AC] focus:outline-none focus:shadow-outline`}
  svg {
    ${tw`w-4 h-4 stroke-3`}
  }
`;

const PrimaryAction = tw.button`rounded-full px-6 py-3 mt-5 text-sm sm:text-base sm:mt-16 sm:px-8 sm:py-4 font-bold shadow transition duration-300 bg-[#0049AC] text-gray-100 focus:outline-none focus:shadow-outline`;

export default ({
  imageSrc = loveIllustrationImageSrc,
  imageRounded = true,
  imageBorder = false,
  imageShadow = false,
  subheading = "We Value Your Experience",
  heading = "",
  description = "Your voice matters to us! Share your experience and help us elevate our service to new heights. We’re grateful for your time and feedback—together, we can keep growing and exceeding expectations.",
  textOnLeft = false
}) => {
  const [reviews, setReviews] = useState([]);
  const [sliderRef, setSliderRef] = useState(null);

  // Dummy fallback data
  const fallbackReviews = [
    {
      stars: 5,
      profileImageSrc: "https://randomuser.me/api/portraits/men/32.jpg",
      heading: "Amazing Experience",
      quote: "This was the best experience ever! The service was incredible and the views were breathtaking.",
      customerName: "John Doe",
      customerTitle: "Travel Blogger"
    },
    {
      stars: 4,
      profileImageSrc: "https://randomuser.me/api/portraits/men/45.jpg",
      heading: "Great Getaway",
      quote: "Had a wonderful time! The staff was friendly and the activities were well organized.",
      customerName: "Jane Smith",
      customerTitle: "Vacationer"
    },
    {
      stars: 5,
      profileImageSrc: "https://randomuser.me/api/portraits/women/32.jpg",
      heading: "An Unforgettable Journey",
      quote: "This trip was absolutely amazing! The team was fantastic, and the destination exceeded my expectations. Highly recommend it!",
      customerName: "Emily Brown",
      customerTitle: "Travel Enthusiast"
    },
    {
      stars: 4,
      profileImageSrc: "https://randomuser.me/api/portraits/men/58.jpg",
      heading: "Incredible Service",
      quote: "The staff was professional and friendly. Everything was well-organized, and I had a relaxing experience. Can't wait for the next one!",
      customerName: "Chris Williams",
      customerTitle: "Business Traveler"
    },
    {
      stars: 3,
      profileImageSrc: "https://randomuser.me/api/portraits/women/11.jpg",
      heading: "Good but Could be Better",
      quote: "It was a good vacation, but there were a few things that could be improved. Overall, a decent experience with some room for improvement.",
      customerName: "Olivia Green",
      customerTitle: "Solo Traveler"
    },
    {
      stars: 5,
      profileImageSrc: "https://randomuser.me/api/portraits/men/71.jpg",
      heading: "Absolutely Perfect!",
      quote: "The entire trip was fantastic. From the moment we arrived, everything was taken care of. This is the ultimate way to travel.",
      customerName: "Michael Davis",
      customerTitle: "Adventure Seeker"
    },
    {
      stars: 4,
      profileImageSrc: "https://randomuser.me/api/portraits/women/27.jpg",
      heading: "Great Experience with Minor Issues",
      quote: "The experience was great, although the weather didn't cooperate at times. Still, a trip I will always remember. Would recommend!",
      customerName: "Sophia Martinez",
      customerTitle: "Family Traveler"
    }
  ];

  // Fetch reviews from API
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${API_URL}/web/home/reviews`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY // Add API key to headers
          }
        });
        const result = await response.json();
        const data = result.data;
        if (result.header.resCode === 200 && data.length > 0) {
          setReviews(data.map(review => ({
            stars: review.rating,
            profileImageSrc: review.profile_pic,
            heading: review.review_text.substring(0, 30), // first 30 characters as heading
            quote: review.review_text,
            customerName: review.user_name,
            customerTitle: review.designation
          })));
        }
      } catch (error) {
        console.error("Error fetching reviews:", error);
      }
    };

    fetchReviews();
  }, []);

  return (
    <Container>
      <br /><br />
      <Row>
        <ImageColumn>
          <PrimaryAction as={Link} to="/pg/write-review">Write Review</PrimaryAction>
          <br /><br />
          <Image src={imageSrc} imageBorder={imageBorder} imageShadow={imageShadow} imageRounded={imageRounded} />
        </ImageColumn>
        <TextColumn textOnLeft={textOnLeft}>
          <Subheading>{subheading}</Subheading>
          <Heading>{heading}</Heading>
          <Description>{description}</Description>
          <TestimonialSlider arrows={false} ref={setSliderRef}>
            {reviews.map((review, index) => (
              <Testimonial key={index}>
                <StarsContainer>
                  {Array.from({ length: review.stars }).map((_, indexIcon) => (
                    <StarIcon key={indexIcon} />
                  ))}
                </StarsContainer>
                <TestimonialHeading>{review.heading}</TestimonialHeading>
                <Quote>{review.quote}</Quote>
                <CustomerInfoAndControlsContainer>
                  <CustomerInfo>
                    <CustomerProfilePicture src={review.profileImageSrc} alt={review.customerName} />
                    <CustomerTextInfo>
                      <CustomerName>{review.customerName}</CustomerName>
                      <CustomerTitle>{review.customerTitle}</CustomerTitle>
                    </CustomerTextInfo>
                  </CustomerInfo>
                </CustomerInfoAndControlsContainer>
              </Testimonial>
            ))}
          </TestimonialSlider>
          <Controls>
            <ControlButton onClick={sliderRef?.slickPrev}>
              <ArrowLeftIcon />
            </ControlButton>
            <div className="divider" />
            <ControlButton onClick={sliderRef?.slickNext}>
              <ArrowRightIcon />
            </ControlButton>
          </Controls>
        </TextColumn>
      </Row>
      <br />
    </Container>
  );
};
